import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useEmployees,
  useInspect,
  useInspectEntity,
  type InspectTagFilter,
  type InspectRowDTO,
  type InspectDayDTO,
  type InspectDayKind,
  type InspectStatsDTO,
  type InspectEntityDayDTO,
  type InspectEmployeeSummaryDTO,
} from '../api/hooks.js';
import { ENTITIES, type EntityId } from '@pointage/shared';
import { EmployeePicker } from '../components/EmployeePicker.js';
import { useUiStore } from '../store/ui.js';

// ─── constants ───────────────────────────────────────────────

const FILTERS: Array<{ f: InspectTagFilter; label: string }> = [
  { f: 'all', label: 'Tous' },
  { f: 'late', label: 'Retards' },
  { f: 'early-leave', label: 'Sortie anticipée' },
  { f: 'late-leave', label: 'Sortie tardive' },
  { f: 'over-8h', label: 'Plus d’heures' },
  { f: 'under-8h', label: 'Moins d’heures' },
  { f: 'tt', label: 'TT' },
  { f: 'absent', label: 'Absences' },
  { f: 'conge', label: 'Congés' },
];

// Colour + label per day kind (composition bar, charts, tooltips).
const KIND_META: Record<InspectDayKind, { label: string; color: string }> = {
  worked: { label: 'Travaillé', color: 'var(--ok)' },
  incomplete: { label: 'Pointage incomplet', color: 'var(--line-strong)' },
  tt: { label: 'Télétravail', color: 'var(--accent)' },
  conge: { label: 'Congé', color: 'var(--info)' },
  recup: { label: 'Récupération', color: 'var(--sgn)' },
  maladie: { label: 'Maladie', color: '#eab308' },
  justifie: { label: 'Justifié', color: 'var(--ink-4)' },
  absent: { label: 'Absence', color: 'var(--bad)' },
  ferie: { label: 'Jour férié', color: 'var(--oct-mid)' },
  weekend: { label: 'Week-end', color: 'var(--surface-3)' },
  'hors-contrat': { label: 'Hors contrat', color: 'var(--surface-3)' },
  futur: { label: 'À venir', color: 'var(--surface-3)' },
};

const DECISION_LABELS: Record<string, string> = {
  TT: 'Télétravail', CONGE: 'Congé', MALADIE: 'Maladie', RECUP: 'Récupération',
  RDV_PERSO: 'RDV perso', AUTRE: 'Autre', PRESENT: 'Présent',
  ABSENCE_INJUSTIFIEE: 'Absence injustifiée',
};

const DOW_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// ─── date helpers ────────────────────────────────────────────

const iso = (d: Date) => d.toISOString().slice(0, 10);
function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
}
const today = () => iso(new Date());

const FR_DAY = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
function fmtDateFr(ymd: string): string {
  return FR_DAY.format(new Date(`${ymd}T00:00:00Z`));
}

function fmtMin(minutes: number): string {
  const m = Math.round(Math.abs(minutes));
  if (m >= 60) return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}`;
  return `${m} min`;
}

type Preset = { key: string; label: string; range: () => { start: string; end: string } };
const PRESETS: Preset[] = [
  { key: '7j', label: '7 j', range: () => ({ start: isoDaysAgo(6), end: today() }) },
  { key: '30j', label: '30 j', range: () => ({ start: isoDaysAgo(29), end: today() }) },
  { key: '90j', label: '90 j', range: () => ({ start: isoDaysAgo(89), end: today() }) },
  {
    key: 'mois', label: 'Mois en cours', range: () => {
      const d = new Date();
      return { start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, end: today() };
    },
  },
  {
    key: 'mois-1', label: 'Mois dernier', range: () => {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      const p = (n: number) => String(n).padStart(2, '0');
      return {
        start: `${first.getFullYear()}-${p(first.getMonth() + 1)}-01`,
        end: `${last.getFullYear()}-${p(last.getMonth() + 1)}-${p(last.getDate())}`,
      };
    },
  },
  { key: 'annee', label: 'Année', range: () => ({ start: `${new Date().getFullYear()}-01-01`, end: today() }) },
];

// ─── animation hooks ─────────────────────────────────────────

/** Animated count-up towards `target` (ease-out cubic, ~750 ms). */
function useCountUp(target: number, duration = 750): number {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = from + (target - from) * eased;
      setVal(v);
      fromRef.current = v;
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// ─── ring gauge ──────────────────────────────────────────────

function RingGauge({ value, color }: { value: number | null; color: string }) {
  const pct = value != null ? Math.max(0, Math.min(1, value)) : 0;
  const R = 30;
  const C = 2 * Math.PI * R;
  const [offset, setOffset] = useState(C);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOffset(C * (1 - pct)));
    return () => cancelAnimationFrame(id);
  }, [pct, C]);
  const shown = useCountUp(value != null ? pct * 100 : 0);
  return (
    <div className="insp-ring">
      <svg viewBox="0 0 72 72" width="72" height="72">
        <circle cx="36" cy="36" r={R} fill="none" stroke="var(--surface-3)" strokeWidth="7" />
        <circle
          cx="36" cy="36" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={offset}
          transform="rotate(-90 36 36)"
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      <div className="insp-ring-val">
        {value != null ? <>{Math.round(shown)}<span>%</span></> : '—'}
      </div>
    </div>
  );
}

// ─── shared KPI blocks (employee + entity views) ─────────────

function StatsHero({ stats, dayWord }: { stats: InspectStatsDTO; dayWord: string }) {
  const totalHoursAnim = useCountUp(stats.totalHours);
  const progress = stats.expectedHours > 0 ? Math.min(1, stats.totalHours / stats.expectedHours) : 0;
  return (
    <div className="insp-hero">
      <div className="insp-card insp-card-hours insp-anim" style={{ animationDelay: '0ms' }}>
        <div className="insp-card-label">Heures travaillées</div>
        <div className="insp-card-big">
          {totalHoursAnim.toFixed(1).replace('.', ',')}<span className="insp-unit">h</span>
          <span className="insp-vs">/ {stats.expectedHours.toFixed(0)} h attendues</span>
        </div>
        <div className="insp-progress">
          <div className="insp-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <div className="insp-card-foot">
          <span className={stats.delta >= 0 ? 'insp-delta up' : 'insp-delta down'}>
            {stats.delta >= 0 ? '+' : '−'}{Math.abs(stats.delta).toFixed(1)} h
          </span>
          {' '}sur {stats.workedDays} {dayWord} travaillés
          {stats.avgDailyHours != null && <> · {stats.avgDailyHours.toFixed(1)} h/j en moyenne</>}
        </div>
      </div>

      <div className="insp-card insp-anim" style={{ animationDelay: '60ms' }}>
        <div className="insp-card-label">Taux de présence</div>
        <RingGauge value={stats.presenceRate} color="var(--ok)" />
        <div className="insp-card-foot">
          {stats.daysPresent} présents / {stats.workableDays} {dayWord} attendus
          {stats.absentCount > 0 && <span className="insp-bad-note"> · {stats.absentCount} abs.</span>}
        </div>
      </div>

      <div className="insp-card insp-anim" style={{ animationDelay: '120ms' }}>
        <div className="insp-card-label">Ponctualité</div>
        <RingGauge value={stats.punctualityRate} color="var(--accent)" />
        <div className="insp-card-foot">
          {stats.lateCount} retard{stats.lateCount > 1 ? 's' : ''}
          {stats.cumulativeLateMinutes > 0 && <> · cumul {fmtMin(stats.cumulativeLateMinutes)}</>}
        </div>
      </div>

      <div className="insp-card insp-anim" style={{ animationDelay: '180ms' }}>
        <div className="insp-card-label">Retard moyen</div>
        <div className="insp-card-big" style={{ color: stats.lateCount > 0 ? 'var(--warn)' : 'var(--ok)' }}>
          {stats.avgLateMinutes != null ? fmtMin(stats.avgLateMinutes) : '0'}
        </div>
        <div className="insp-card-foot">
          {stats.lateCount > 0
            ? `par retard, sur ${stats.lateCount} retard${stats.lateCount > 1 ? 's' : ''}`
            : 'aucun retard sur la période 🎉'}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, foot, delay }: { label: string; value: React.ReactNode; foot?: string; delay: number }) {
  return (
    <div className="insp-mini insp-anim" style={{ animationDelay: `${delay}ms` }}>
      <div className="insp-mini-label">{label}</div>
      <div className="insp-mini-val">{value}</div>
      {foot && <div className="insp-mini-foot">{foot}</div>}
    </div>
  );
}

function StatsMinis({ stats }: { stats: InspectStatsDTO }) {
  return (
    <div className="insp-minis">
      <MiniStat
        label="Arrivée moyenne" delay={80}
        value={stats.avgArrival ?? '—'}
        foot={`attendu ${stats.expectedStart}`}
      />
      <MiniStat
        label="Sortie moyenne" delay={120}
        value={stats.avgDeparture ?? '—'}
        foot={`attendu ${stats.expectedEnd}`}
      />
      <MiniStat
        label="Heures supp." delay={160}
        value={<span style={{ color: 'var(--ok)' }}>+{fmtMin(stats.overtimeMinutes)}</span>}
        foot="cumul des dépassements"
      />
      <MiniStat
        label="Heures manquantes" delay={200}
        value={<span style={{ color: stats.undertimeMinutes > 0 ? 'var(--bad)' : 'var(--ink)' }}>−{fmtMin(stats.undertimeMinutes)}</span>}
        foot="cumul des journées courtes"
      />
      <MiniStat
        label="Arrivée la + tôt" delay={240}
        value={stats.earliestArrival ?? '—'}
        foot={`sortie la + tardive ${stats.latestDeparture ?? '—'}`}
      />
      <MiniStat
        label="Pointages incomplets" delay={280}
        value={<span style={{ color: stats.incompleteCount > 0 ? 'var(--warn)' : 'var(--ink)' }}>{stats.incompleteCount}</span>}
        foot="une seule badgeuse sur la journée"
      />
    </div>
  );
}

// ─── composition bar ─────────────────────────────────────────

const COMPO_ORDER: InspectDayKind[] = [
  'worked', 'incomplete', 'tt', 'conge', 'recup', 'maladie', 'justifie', 'absent', 'ferie', 'weekend',
];

function CompositionBar({ stats, hideCalendar, unit }: { stats: InspectStatsDTO; hideCalendar?: boolean; unit: string }) {
  const items = useMemo(() => {
    const by: Partial<Record<InspectDayKind, number>> = {
      worked: stats.workedDays,
      incomplete: stats.incompleteCount,
      tt: stats.ttCount,
      conge: stats.congeCount,
      recup: stats.recupCount,
      maladie: stats.maladieCount,
      justifie: stats.justifiedCount,
      absent: stats.absentCount,
      ferie: hideCalendar ? 0 : stats.holidayCount,
      weekend: hideCalendar ? 0 : stats.weekendCount,
    };
    return COMPO_ORDER.map((k) => ({ kind: k, n: by[k] ?? 0 })).filter((x) => x.n > 0);
  }, [stats, hideCalendar]);
  const total = items.reduce((a, b) => a + b.n, 0);
  if (total === 0) return null;
  return (
    <div className="card insp-anim" style={{ animationDelay: '120ms' }}>
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 4 }}>
        <div className="card-title">Composition de la période<span className="card-title-count">{total} {unit}</span></div>
      </div>
      <div style={{ padding: '0 18px 16px' }}>
        <div className="insp-compo" data-testid="inspect-compo">
          {items.map((it, i) => (
            <div
              key={it.kind}
              className="insp-compo-seg"
              style={{
                width: `${(it.n / total) * 100}%`,
                background: KIND_META[it.kind].color,
                animationDelay: `${150 + i * 60}ms`,
              }}
              title={`${KIND_META[it.kind].label} · ${it.n}`}
            />
          ))}
        </div>
        <div className="insp-legend">
          {items.map((it) => (
            <span key={it.kind} className="insp-legend-item">
              <i style={{ background: KIND_META[it.kind].color }} />
              {KIND_META[it.kind].label}
              <b>{it.n}</b>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── daily hours chart (employee view) ───────────────────────

function DailyChart({ days, expected }: { days: InspectDayDTO[]; expected: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = days.filter((d) => d.kind !== 'hors-contrat');
  const n = shown.length;
  if (n === 0) return null;
  const maxH = Math.max(expected + 2, ...shown.map((d) => d.hours ?? 0)) + 0.5;
  const H = 100;
  const y = (h: number) => H - (h / maxH) * (H - 12);
  const hoverDay = hover != null ? shown[hover] : null;

  return (
    <div className="card insp-anim" style={{ animationDelay: '180ms', overflow: 'visible' }}>
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 4 }}>
        <div className="card-title">Heures par jour</div>
        <span className="insp-chart-hint">ligne pointillée = {expected} h attendues</span>
      </div>
      <div className="insp-chart-wrap">
        {hoverDay && (
          <div
            className="insp-tip"
            style={{
              left: `${((hover! + 0.5) / n) * 100}%`,
              transform: `translateX(${hover! > n * 0.75 ? '-100%' : hover! < n * 0.25 ? '0' : '-50%'})`,
            }}
          >
            <div className="insp-tip-date">{fmtDateFr(hoverDay.date)}</div>
            <div className="insp-tip-kind">
              <i style={{ background: KIND_META[hoverDay.kind].color }} />
              {KIND_META[hoverDay.kind].label}
              {hoverDay.hours != null && <b> · {hoverDay.hours.toFixed(1)} h</b>}
            </div>
            {hoverDay.checkIn && (
              <div className="insp-tip-times">{hoverDay.checkIn} → {hoverDay.checkOut ?? '?'}</div>
            )}
            {hoverDay.lateMinutes != null && (
              <div className="insp-tip-late">Retard {fmtMin(hoverDay.lateMinutes)}</div>
            )}
          </div>
        )}
        <svg
          viewBox={`0 0 ${n} ${H}`}
          preserveAspectRatio="none"
          className="insp-chart"
          onMouseLeave={() => setHover(null)}
        >
          <line
            x1="0" x2={n} y1={y(expected)} y2={y(expected)}
            stroke="var(--ink-4)" strokeWidth="1" strokeDasharray="4 5"
            vectorEffect="non-scaling-stroke" opacity="0.7"
          />
          {shown.map((d, i) => {
            const meta = KIND_META[d.kind];
            const isBar = d.hours != null;
            return (
              <g key={d.date}>
                {isBar ? (
                  <rect
                    className="insp-bar"
                    x={i + 0.12} width={0.76}
                    y={y(d.hours!)} height={H - 6 - y(d.hours!)}
                    rx="0.08"
                    fill={d.lateMinutes != null ? 'var(--warn)' : meta.color}
                    opacity={hover === i ? 1 : 0.85}
                    style={{ animationDelay: `${Math.min(i * 7, 500)}ms` }}
                  />
                ) : (
                  d.kind !== 'futur' && (
                    <rect
                      x={i + 0.18} width={0.64} y={H - 5.5} height={3.5} rx="0.1"
                      fill={meta.color}
                      opacity={d.kind === 'weekend' ? 0.9 : 0.95}
                    />
                  )
                )}
                <rect
                  x={i} width={1} y="0" height={H} fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
              </g>
            );
          })}
        </svg>
        <div className="insp-chart-axis">
          <span>{fmtDateFr(shown[0].date)}</span>
          <span>{fmtDateFr(shown[n - 1].date)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── headcount chart (entity view) ───────────────────────────

const HEAD_SEGS: Array<{ key: 'presents' | 'incomplets' | 'tt' | 'conges' | 'autres' | 'absents'; kind: InspectDayKind; label: string }> = [
  { key: 'presents', kind: 'worked', label: 'Présents' },
  { key: 'incomplets', kind: 'incomplete', label: 'Pointage incomplet' },
  { key: 'tt', kind: 'tt', label: 'Télétravail' },
  { key: 'conges', kind: 'conge', label: 'Congés' },
  { key: 'autres', kind: 'justifie', label: 'Récup / maladie / justifié' },
  { key: 'absents', kind: 'absent', label: 'Absents' },
];

function HeadcountChart({ days }: { days: InspectEntityDayDTO[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const n = days.length;
  if (n === 0) return null;
  const totalOf = (d: InspectEntityDayDTO) =>
    d.presents + d.incomplets + d.tt + d.conges + d.autres + d.absents;
  const maxN = Math.max(1, ...days.map(totalOf));
  const H = 100;
  const scale = (H - 12) / maxN;
  const hoverDay = hover != null ? days[hover] : null;

  return (
    <div className="card insp-anim" style={{ animationDelay: '180ms', overflow: 'visible' }}>
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 4 }}>
        <div className="card-title">Effectif par jour</div>
        <span className="insp-chart-hint">présents · TT · congés · absents</span>
      </div>
      <div className="insp-chart-wrap">
        {hoverDay && (
          <div
            className="insp-tip"
            style={{
              left: `${((hover! + 0.5) / n) * 100}%`,
              transform: `translateX(${hover! > n * 0.75 ? '-100%' : hover! < n * 0.25 ? '0' : '-50%'})`,
            }}
          >
            <div className="insp-tip-date">{fmtDateFr(hoverDay.date)}</div>
            {hoverDay.weekend || hoverDay.ferie ? (
              <div className="insp-tip-kind">
                <i style={{ background: KIND_META[hoverDay.weekend ? 'weekend' : 'ferie'].color }} />
                {hoverDay.weekend ? 'Week-end' : 'Jour férié'}
              </div>
            ) : hoverDay.futur ? (
              <div className="insp-tip-kind"><i style={{ background: 'var(--surface-3)' }} />À venir</div>
            ) : (
              <>
                {HEAD_SEGS.filter((s) => hoverDay[s.key] > 0).map((s) => (
                  <div key={s.key} className="insp-tip-kind">
                    <i style={{ background: KIND_META[s.kind].color }} />
                    {s.label}<b> · {hoverDay[s.key]}</b>
                  </div>
                ))}
                {hoverDay.totalHours > 0 && (
                  <div className="insp-tip-times">
                    {hoverDay.totalHours.toFixed(0)} h cumulées{hoverDay.avgHours != null ? ` · ${hoverDay.avgHours.toFixed(1)} h/présent` : ''}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <svg
          viewBox={`0 0 ${n} ${H}`}
          preserveAspectRatio="none"
          className="insp-chart"
          onMouseLeave={() => setHover(null)}
        >
          {days.map((d, i) => {
            if (d.weekend || d.ferie) {
              return (
                <g key={d.date}>
                  <rect
                    x={i + 0.18} width={0.64} y={H - 5.5} height={3.5} rx="0.1"
                    fill={KIND_META[d.weekend ? 'weekend' : 'ferie'].color}
                  />
                  <rect x={i} width={1} y="0" height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
                </g>
              );
            }
            let yCursor = H - 6;
            const segs = HEAD_SEGS.map((s) => {
              const v = d[s.key];
              const h = v * scale;
              yCursor -= h;
              return v > 0 ? (
                <rect
                  key={s.key}
                  className="insp-bar"
                  x={i + 0.12} width={0.76}
                  y={yCursor} height={h}
                  fill={KIND_META[s.kind].color}
                  opacity={hover === i ? 1 : 0.85}
                  style={{ animationDelay: `${Math.min(i * 7, 500)}ms` }}
                />
              ) : null;
            });
            return (
              <g key={d.date}>
                {segs}
                <rect x={i} width={1} y="0" height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
              </g>
            );
          })}
        </svg>
        <div className="insp-chart-axis">
          <span>{fmtDateFr(days[0].date)}</span>
          <span>{fmtDateFr(days[n - 1].date)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── weekday profile (employee view) ─────────────────────────

function WeekdayProfile({ days }: { days: InspectDayDTO[] }) {
  const profile = useMemo(() => {
    const acc = new Map<number, { sum: number; n: number }>();
    for (const d of days) {
      if (d.kind !== 'worked' || d.hours == null) continue;
      const e = acc.get(d.dow) ?? { sum: 0, n: 0 };
      e.sum += d.hours;
      e.n++;
      acc.set(d.dow, e);
    }
    return [0, 1, 2, 3, 4, 5, 6]
      .filter((dow) => acc.has(dow))
      .map((dow) => ({ dow, avg: acc.get(dow)!.sum / acc.get(dow)!.n, n: acc.get(dow)!.n }));
  }, [days]);
  const max = Math.max(1, ...profile.map((p) => p.avg));
  return (
    <div className="card insp-anim" style={{ animationDelay: '240ms' }}>
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 4 }}>
        <div className="card-title">Profil hebdomadaire</div>
        <span className="insp-chart-hint">moyenne d’heures travaillées</span>
      </div>
      <div className="insp-week">
        {profile.length === 0 && <div className="insp-empty">Aucun jour travaillé.</div>}
        {profile.map((p, i) => (
          <div key={p.dow} className="insp-week-row">
            <span className="insp-week-day">{DOW_LABELS[p.dow]}</span>
            <div className="insp-week-track">
              <div
                className="insp-week-fill"
                style={{ width: `${(p.avg / max) * 100}%`, animationDelay: `${250 + i * 70}ms` }}
              />
            </div>
            <span className="insp-week-val">{p.avg.toFixed(1)} h</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── absence profile (entity view side panel) ────────────────

function AbsenceWeekdayProfile({ days }: { days: InspectEntityDayDTO[] }) {
  const profile = useMemo(() => {
    const acc = new Map<number, { abs: number; n: number }>();
    for (const d of days) {
      if (d.weekend || d.ferie || d.futur) continue;
      const e = acc.get(d.dow) ?? { abs: 0, n: 0 };
      e.abs += d.absents;
      e.n++;
      acc.set(d.dow, e);
    }
    return [0, 1, 2, 3, 4, 5, 6]
      .filter((dow) => acc.has(dow))
      .map((dow) => ({ dow, avg: acc.get(dow)!.abs / acc.get(dow)!.n }));
  }, [days]);
  const max = Math.max(0.5, ...profile.map((p) => p.avg));
  return (
    <div className="card insp-anim" style={{ animationDelay: '240ms' }}>
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 4 }}>
        <div className="card-title">Absences par jour de semaine</div>
        <span className="insp-chart-hint">moyenne d’absents</span>
      </div>
      <div className="insp-week">
        {profile.length === 0 && <div className="insp-empty">Aucune donnée.</div>}
        {profile.map((p, i) => (
          <div key={p.dow} className="insp-week-row">
            <span className="insp-week-day">{DOW_LABELS[p.dow]}</span>
            <div className="insp-week-track">
              <div
                className="insp-week-fill insp-week-fill-bad"
                style={{ width: `${(p.avg / max) * 100}%`, animationDelay: `${250 + i * 70}ms` }}
              />
            </div>
            <span className="insp-week-val">{p.avg.toFixed(1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── entity employee table ───────────────────────────────────

type SortKey = 'name' | 'workedDays' | 'totalHours' | 'delta' | 'presenceRate' | 'lateCount' | 'absentCount';

function EmployeeTable({ rows, onPick, rangeLabel }: {
  rows: InspectEmployeeSummaryDTO[];
  onPick: (id: string) => void;
  rangeLabel: string;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('absentCount');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * sortDir;
      const av = (a[sortKey] ?? -1) as number;
      const bv = (b[sortKey] ?? -1) as number;
      return (av - bv) * sortDir || a.name.localeCompare(b.name);
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const clickSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(k === 'name' ? 1 : -1); }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === -1 ? ' ↓' : ' ↑') : '');

  const csv = () => {
    const head = ['Code', 'Nom', 'Entité', 'Jours travaillés', 'Heures', 'Attendu', 'Écart', 'Présence %', 'Retards', 'Retard moyen (min)', 'Absences', 'Arrivée moyenne'];
    const lines = sorted.map((e) =>
      [
        e.code, e.name, e.entityShort, e.workedDays, e.totalHours, e.expectedHours, e.delta,
        e.presenceRate != null ? Math.round(e.presenceRate * 100) : '',
        e.lateCount, e.avgLateMinutes ?? '', e.absentCount, e.avgArrival ?? '',
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','),
    );
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspection-equipe-${rangeLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card insp-anim" style={{ padding: 0, animationDelay: '300ms', marginTop: 12 }} data-testid="inspect-entity-table">
      <div className="card-header">
        <div className="card-title">
          Par employé
          <span className="card-title-count">{rows.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="insp-chart-hint">cliquez sur une ligne pour le détail</span>
          <button className="btn btn-sm btn-ghost" onClick={csv}>Exporter CSV</button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => clickSort('name')}>Employé{arrow('name')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('workedDays')}>J. trav.{arrow('workedDays')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('totalHours')}>Heures{arrow('totalHours')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('delta')}>Écart{arrow('delta')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('presenceRate')}>Présence{arrow('presenceRate')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('lateCount')}>Retards{arrow('lateCount')}</th>
            <th className="num" style={{ cursor: 'pointer' }} onClick={() => clickSort('absentCount')}>Absences{arrow('absentCount')}</th>
            <th className="num">Arrivée moy.</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e) => (
            <tr key={e.id} className="insp-emp-row" onClick={() => onPick(e.id)} data-testid={`inspect-entity-row-${e.code}`}>
              <td>
                <strong>{e.name}</strong>
                <span style={{ color: 'var(--ink-4)', fontSize: 11, marginLeft: 7 }}>
                  <span className={`pill ${e.entityShort.toLowerCase()}`} style={{ padding: '1px 6px', fontSize: 10, marginRight: 5 }}>{e.entityShort}</span>
                  {e.role ?? ''}
                </span>
              </td>
              <td className="num">{e.workedDays}</td>
              <td className="num">{e.totalHours.toFixed(1)}<span style={{ color: 'var(--ink-3)', fontSize: 11 }}>h</span></td>
              <td className="num">
                <span style={{ color: e.delta >= 0 ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }}>
                  {e.delta >= 0 ? '+' : '−'}{Math.abs(e.delta).toFixed(1)}
                </span>
              </td>
              <td className="num">{e.presenceRate != null ? `${Math.round(e.presenceRate * 100)} %` : '—'}</td>
              <td className="num">
                {e.lateCount > 0
                  ? <span style={{ color: 'var(--warn)', fontWeight: 600 }}>{e.lateCount}{e.avgLateMinutes != null ? ` · ${e.avgLateMinutes}m` : ''}</span>
                  : '0'}
              </td>
              <td className="num">
                {e.absentCount > 0
                  ? <span style={{ color: 'var(--bad)', fontWeight: 600 }}>{e.absentCount}</span>
                  : '0'}
              </td>
              <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{e.avgArrival ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 24 }}>Aucun employé dans ce périmètre.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── small pieces ────────────────────────────────────────────

function pointageText(row: InspectRowDTO): string {
  if (row.pointage.checkIn && row.pointage.checkOut) return `${row.pointage.checkIn} → ${row.pointage.checkOut}`;
  if (row.pointage.checkIn) return `${row.pointage.checkIn} → ?`;
  if (row.pointage.checkOut) return `? → ${row.pointage.checkOut}`;
  return '—';
}

// ─── page ────────────────────────────────────────────────────

export function InspectPage() {
  const employees = useEmployees();
  const entityId = useUiStore((s) => s.currentEntityId);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(isoDaysAgo(29));
  const [endDate, setEndDate] = useState(today());
  const [tagFilter, setTagFilter] = useState<InspectTagFilter>('all');

  // The picker follows the app-wide entity selector (EntityBar).
  const scoped = useMemo(
    () => (employees.data ?? []).filter((e) => entityId === 'all' || e.entityId === entityId),
    [employees.data, entityId],
  );
  // Vue d'ensemble by default; a picked employee opens the detail view.
  // Falls back to the overview when the pick leaves the entity scope.
  const effectiveId =
    employeeId && scoped.some((e) => e.id === employeeId) ? employeeId : null;
  const overviewMode = effectiveId == null;

  const inspect = useInspect({
    employeeId: effectiveId, startDate, endDate, tagFilter, enabled: !overviewMode,
  });
  const overview = useInspectEntity({
    entityId, startDate, endDate, enabled: overviewMode,
  });
  const data = overviewMode ? undefined : inspect.data;
  const entData = overviewMode ? overview.data : undefined;
  const stats = data?.stats;

  const entityLabel =
    entityId === 'all'
      ? 'toutes les entités'
      : ENTITIES.find((e) => e.id === (entityId as EntityId))?.name ?? entityId;

  const activePreset = PRESETS.find((p) => {
    const r = p.range();
    return r.start === startDate && r.end === endDate;
  })?.key;

  const csv = useMemo(() => () => {
    if (!data) return;
    const head = ['Date', 'Entrée', 'Sortie', 'Heures', 'Tags', 'Décision', 'Prévenu', 'Note'];
    const lines = data.rows.map((r) =>
      [
        r.date,
        r.pointage.checkIn ?? '',
        r.pointage.checkOut ?? '',
        r.hoursReal ?? '',
        r.tags.map((t) => t.label).join(' / '),
        r.decisionType ?? '',
        r.notifiedInAdvance ?? '',
        (r.note ?? '').replace(/"/g, '""'),
      ]
        .map((c) => `"${c}"`)
        .join(','),
    );
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspection-${data.employee.code}-${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, startDate, endDate]);

  const isLoading = overviewMode ? overview.isLoading : inspect.isLoading;
  const isError = overviewMode ? overview.isError : inspect.isError;

  return (
    <>
      <div className="page-header">
        <div className="page-eyebrow">Analyse détaillée · période & tags</div>
        <h1 className="page-title">
          Inspection <em>{overviewMode ? 'équipe' : 'employé'}</em>
        </h1>
        <p className="page-subtitle">
          {overviewMode
            ? <>Vue d’ensemble de <strong>{entityLabel}</strong> — choisissez un employé pour le détail. Les KPI d’heures ne comptent que les jours réellement travaillés.</>
            : <>Les KPI d’heures ne comptent que les jours réellement travaillés — week-ends, fériés, TT, congés, récup et absences sont exclus du réalisé comme de l’attendu.</>}
        </p>
      </div>

      {/* Overview toggle + employee + range pickers */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <button
          className={overviewMode ? 'insp-scope-btn active' : 'insp-scope-btn'}
          onClick={() => setEmployeeId(null)}
          data-testid="inspect-overview-btn"
        >
          <span className="insp-scope-ico" aria-hidden="true">⌂</span>
          Vue d’ensemble
        </button>

        <EmployeePicker
          employees={scoped}
          value={effectiveId}
          onChange={setEmployeeId}
          testId="inspect-employee"
        />

        <div className="inspect-range">
          <div>
            <div className="inspect-range-label">Du</div>
            <input className="set-input" style={{ width: 130 }} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="inspect-start" />
          </div>
          <span style={{ color: 'var(--ink-4)', fontSize: 18 }}>→</span>
          <div>
            <div className="inspect-range-label">Au</div>
            <input className="set-input" style={{ width: 130 }} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="inspect-end" />
          </div>
        </div>

        <div className="insp-presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={activePreset === p.key ? 'insp-preset active' : 'insp-preset'}
              onClick={() => { const r = p.range(); setStartDate(r.start); setEndDate(r.end); }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data && !entData && <p className="page-placeholder">Chargement…</p>}
      {isError && <p className="page-placeholder" data-testid="inspect-error">Impossible de charger l'analyse.</p>}

      {/* ═══ Vue d'ensemble (entity aggregate) ═══ */}
      {overviewMode && entData && (
        <div key={`ent-${entityId}-${startDate}-${endDate}`} data-testid="inspect-overview">
          <div className="insp-scope-line insp-anim">
            <strong>{entData.scope.employeeCount}</strong>&nbsp;employés actifs
            {entData.scope.entityShort && <>&nbsp;· <span className={`pill ${entData.scope.entityShort.toLowerCase()}`} style={{ padding: '1px 8px' }}>{entData.scope.entityShort}</span></>}
            <span style={{ color: 'var(--ink-4)' }}>&nbsp;· période {fmtDateFr(entData.range.start)} → {fmtDateFr(entData.range.end)}</span>
          </div>
          <StatsHero stats={entData.stats} dayWord="jours-homme" />
          <StatsMinis stats={entData.stats} />
          <CompositionBar stats={entData.stats} hideCalendar unit="jours-homme" />
          <div className="insp-charts">
            <HeadcountChart days={entData.days} />
            <AbsenceWeekdayProfile days={entData.days} />
          </div>
          <EmployeeTable
            rows={entData.employees}
            onPick={setEmployeeId}
            rangeLabel={`${startDate}_${endDate}`}
          />
        </div>
      )}

      {/* ═══ Détail employé ═══ */}
      {!overviewMode && data && stats && (
        <div key={`${data.employee.id}-${startDate}-${endDate}`}>
          <StatsHero stats={stats} dayWord="jours" />
          <StatsMinis stats={stats} />
          <CompositionBar stats={stats} unit="jours" />

          <div className="insp-charts">
            <DailyChart days={data.days} expected={stats.expectedDayHours} />
            <WeekdayProfile days={data.days} />
          </div>

          {/* Filter chips */}
          <div style={{ margin: '18px 0 16px' }}>
            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10 }}>
              Filtrer par tag
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {FILTERS.map((it) => (
                <button
                  key={it.f}
                  className={tagFilter === it.f ? 'filter-chip active' : 'filter-chip'}
                  onClick={() => setTagFilter(it.f)}
                  data-testid={`filter-${it.f}`}
                >
                  {it.label}
                  <span className="filter-chip-count">{data.counts[it.f] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Results table */}
          <div className="card insp-anim" style={{ padding: 0, animationDelay: '300ms' }} data-testid="inspect-results">
            <div className="card-header">
              <div className="card-title">
                Résultats
                <span className="card-title-count">{data.rows.length} jours</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm btn-ghost" onClick={csv} data-testid="inspect-csv">Exporter CSV</button>
                <button className="btn btn-sm btn-ghost" onClick={() => window.print()}>Rapport PDF</button>
              </div>
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Date</th>
                  <th style={{ width: '14%' }}>Pointage</th>
                  <th className="num" style={{ width: '10%' }}>Heures</th>
                  <th style={{ width: '18%' }}>Tags</th>
                  <th style={{ width: '15%' }}>Décision</th>
                  <th style={{ width: '11%' }}>Prévenu&nbsp;?</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.date} data-testid={`inspect-row-${r.date}`}>
                    <td>
                      <strong>{fmtDateFr(r.date)}</strong>
                      <span style={{ color: 'var(--ink-4)', fontSize: 11, marginLeft: 6, fontFamily: 'var(--mono)' }}>{r.date.slice(0, 4)}</span>
                    </td>
                    <td><span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-2)' }}>{pointageText(r)}</span></td>
                    <td className="num">{r.hoursReal != null ? r.hoursReal.toFixed(1) : '—'}<span style={{ color: 'var(--ink-3)', fontSize: 11 }}>h</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.tags.map((t, i) => (
                          <span key={i} className={`tag ${t.type === 'justified' ? 'tt' : t.type}`}>{t.label}</span>
                        ))}
                      </div>
                    </td>
                    <td>{r.decisionType ? <span className="tag tt">{DECISION_LABELS[r.decisionType] ?? r.decisionType}</span> : <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>—</span>}</td>
                    <td>
                      {r.notifiedInAdvance === 'YES' ? <span className="notif-pick yes">Oui</span>
                        : r.notifiedInAdvance === 'NO' ? <span className="notif-pick no">Non</span>
                        : <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>—</span>}
                    </td>
                    <td><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.note ?? ''}</span></td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 24 }}>Aucun jour correspondant.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
